/**
 * Onboarding context — device id + currently-active child.
 *
 * Scope: lives inside the (onboarding) route group's _layout.tsx so all
 * 7 onboarding screens (Step 0-6) can read the same state without
 * round-tripping to the server for the child profile id.
 *
 * Lifecycle:
 *  - On first render: deviceId is created (localStorage-backed) and held
 *    in state. currentChild is null until the Child Profile form (Step 2)
 *    successfully POSTs /v1/child.
 *  - Step 2 form calls setCurrentChild() on API success.
 *  - Subsequent screens (Steps 3-6) read currentChild to build quiz and
 *    preference payloads.
 *
 * Outside the onboarding flow (home / library / parent zone), this
 * context is not provided. Those screens will fetch via the api client
 * directly. We can lift this to the root layout later if we want
 * cross-flow state sharing.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { getOrCreateDeviceId } from '@/lib/device-id';
import type { ChildProfile } from '@funpaper/shared';

type OnboardingContextValue = {
  deviceId: string;
  currentChild: ChildProfile | null;
  setCurrentChild: (child: ChildProfile | null) => void;
  resetOnboarding: () => void;
};

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

type OnboardingProviderProps = {
  children: ReactNode;
};

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  // getOrCreateDeviceId reads localStorage; safe to call once at mount.
  const deviceId = useMemo(() => getOrCreateDeviceId(), []);
  const [currentChild, setCurrentChildState] = useState<ChildProfile | null>(null);

  const setCurrentChild = useCallback((child: ChildProfile | null) => {
    setCurrentChildState(child);
  }, []);

  const resetOnboarding = useCallback(() => {
    setCurrentChildState(null);
  }, []);

  const value = useMemo<OnboardingContextValue>(
    () => ({ deviceId, currentChild, setCurrentChild, resetOnboarding }),
    [deviceId, currentChild, setCurrentChild, resetOnboarding],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error(
      'useOnboarding() must be called from within an <OnboardingProvider>',
    );
  }
  return ctx;
}

/**
 * Safe variant — returns null when not in an OnboardingProvider.
 * Useful for components rendered in both onboarding and other flows.
 */
export function useOnboardingOptional(): OnboardingContextValue | null {
  return useContext(OnboardingContext);
}
